import { Clipboard } from '@angular/cdk/clipboard';
import { Component, Inject, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { filter, finalize } from 'rxjs/operators';
import { CustomRouter } from 'src/admin-front/ajs-upgraded-providers';
import { AlcmeonContextService } from 'src/app/core/alcmeon-context/alcmeon-context.service';
import { AlcmeonCardFooter } from 'src/app/shared/components/card-footer/models/alcmeon-card-footer.model';
import { Choice } from 'src/app/shared/components/select/choice.model';
import { BasicService } from 'src/app/shared/services/basic.service';
import { urlValidator } from 'src/app/shared/validators/url.validator';

import { UserV2 } from '../../users/shared/models';
import { AlcmeonAiService } from '../alcmeon-ai.service';
import { AiAgentModeEnum, AiAgentTypeEnum } from '../shared/enums';
import { AiAgent, KnowledgeDocument } from '../shared/models';

@Component({
  selector: 'app-ai-agent-edit',
  templateUrl: './ai-agent-edit.component.html',
  styleUrls: ['./ai-agent-edit.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class AiAgentEditComponent implements OnInit, OnDestroy {
  aiAgent: AiAgent;
  aiAgentModeEnum = AiAgentModeEnum;
  companyName: string;
  footerContent: AlcmeonCardFooter;
  id: number;
  isCreation: boolean;
  isGaiaExists: boolean;
  knowledgeDocs: KnowledgeDocument[];
  title: string;
  usersMap: Map<number, UserV2>;

  gaia = this.basicService.translate('parameters/alcmeon-ai/edit-agent/agent-type/gaia');
  thirdParty = this.basicService.translate('parameters/alcmeon-ai/edit-agent/agent-type/third-party');

  knowledgeDocsItems: Choice[];
  typeItems: Choice[];

  // default values
  answerStyleValue: string;
  assistantContextValue: string;
  companyContextValue: string;

  // form
  externalUrlValidators = [Validators.required, urlValidator, Validators.pattern(/^https:\/\//)];

  form: FormGroup = this.formBuilder.group({
    name: ['', Validators.required],
    isActive: [false, Validators.required],
    mode: [AiAgentModeEnum.aissitant, Validators.required],
    type: ['', Validators.required],

    // GAIA
    gaiaData: this.formBuilder.group({
      datasourceIds: [[]],
      withSource: [false],
      companyContext: ['', Validators.required],
      assistantContext: ['', Validators.required],
      answerStyle: ['', Validators.required],
      checkToxic: [{ value: false, disabled: true }],
      anonymize: [false],
      allowListString: ['', Validators.required],
    }),

    // Third party
    externalUrl: ['', this.externalUrlValidators],
    apiKey: [''],
    publicKey: [{ value: '', disabled: true }], // TODO check if stay disabled when switching between gaia and 3rd party
  });

  private subscriptions = new Subscription();

  constructor(
    private alcmeonAiService: AlcmeonAiService,
    private alcmeonContextService: AlcmeonContextService,
    private basicService: BasicService,
    private clipboard: Clipboard,
    private customRouter: CustomRouter,
    private formBuilder: FormBuilder,
    @Inject('$stateParams') private $stateParams: any,
    @Inject('TopToolbarFactory') private topToolbarService: any
  ) {}

  ngOnInit(): void {
    this.basicService.showSpinner();
    this.id = this.$stateParams.id;
    this.isCreation = this.customRouter.current.data.create;
    this.title = this.isCreation
      ? this.basicService.translate('parameters/alcmeon-ai/edit-agent/create-title')
      : this.basicService.translate('parameters/alcmeon-ai/edit-agent/edit-title');
    this.topToolbarService.setTitle(
      `${this.basicService.translate('parameters/alcmeon-ai/title')} › ${this.title}`,
      null
    );
    this.usersMap = this.alcmeonContextService.getCompany().usersMap;

    this.subscriptions.add(
      this.form
        .get('mode')
        .valueChanges.pipe(filter(() => this.form.get('type').value === AiAgentTypeEnum.GAIA))
        .subscribe(mode => {
          if (mode === AiAgentModeEnum.aiBot) {
            this.form.get('gaiaData.checkToxic').enable();
            this.form.get('gaiaData.checkToxic').setValue(true);
          } else {
            this.form.get('gaiaData.checkToxic').disable();
            this.form.get('gaiaData.checkToxic').setValue(false);
          }
        })
    );

    this.initValues();

    this.subscriptions.add(
      this.form.get('type').valueChanges.subscribe(type => {
        if (type === AiAgentTypeEnum.GAIA) {
          this.enableGaia();
        } else {
          this.enableThirdParty();
        }
        this.form.updateValueAndValidity();
      })
    );

    this.subscriptions.add(
      this.form.get('gaiaData.anonymize').valueChanges.subscribe(anonymize => {
        const allowListString = this.form.get('gaiaData.allowListString');

        if (anonymize) {
          allowListString.enable({ onlySelf: true });
          if (!allowListString.value) {
            allowListString.setValue(this.companyName);
          }
        } else {
          allowListString.disable({ onlySelf: true });
        }
        allowListString.updateValueAndValidity({ onlySelf: true });
      })
    );
  }

  /**
   * No call to .pipe(finalize(() => this.basicService.hideSpinner())),
   * because we have others http calls in initIsGaia(), where hideSpinner() is called.
   *
   * But in edition of third party agent, initIsGaia() is not called,
   * so we have to call hideSpinner() here for nominal case and error.
   */
  private initValues() {
    // when in edit mode, we want to disable the type that was not created
    this.typeItems = [
      { id: 0, label: this.gaia, disabled: false },
      { id: 1, label: this.thirdParty, disabled: false },
    ];
    if (this.isCreation) {
      // default values
      this.form.get('isActive').setValue(false);
      this.form.get('mode').setValue(AiAgentModeEnum.aissitant);
      this.form.get('externalUrl').setValue('https://');
      this.initIsGaia();
    } else {
      this.alcmeonAiService.getAiAgent(this.id).subscribe({
        next: aiAgent => {
          // if the back sends me empty object I know that AI Agent does not exist
          if (Object.keys(aiAgent).length) {
            this.aiAgent = aiAgent;
            this.form.patchValue(this.aiAgent);

            if (this.aiAgent.type === 0) {
              if (this.aiAgent.gaiaData.anonymize) {
                this.form.get('gaiaData.allowListString').setValue((this.aiAgent.gaiaData.allowList || []).join('\n'));
              }

              const newTypeItems = this.typeItems.map(item => (item.id === 1 ? { ...item, disabled: true } : item));
              this.typeItems = newTypeItems;
              this.initIsGaia();
            } else {
              const newTypeItems = this.typeItems.map(item => (item.id === 0 ? { ...item, disabled: true } : item));
              this.typeItems = newTypeItems;
              this.basicService.hideSpinner();
            }

            // card footer
            this.updateFooter(this.aiAgent);
          } else {
            this.basicService.toastAlert('parameters/alcmeon-ai/edit-agent/error/no-ai-agent');
            this.goToAiAgents();
          }
        },
        error: () => {
          this.basicService.hideSpinner();
        },
      });
    }
    this.initDefaultValuesGaia();
  }

  private initDefaultValuesGaia() {
    this.companyName = this.alcmeonContextService.getCompany().name;
    this.companyContextValue = this.basicService
      .translate('parameters/alcmeon-ai/edit-agent/company-context/default-value')
      .replace('{{company}}', this.companyName);
    this.assistantContextValue = this.basicService
      .translate('parameters/alcmeon-ai/edit-agent/assistant-context/default-value')
      .replace('{{company}}', this.companyName);
    this.answerStyleValue = this.basicService.translate(
      'parameters/alcmeon-ai/edit-agent/style-of-answer/default-value'
    );

    this.form.get('gaiaData.withSource').setValue(false);
    this.form.get('gaiaData.companyContext').setValue(this.companyContextValue);
    this.form.get('gaiaData.assistantContext').setValue(this.assistantContextValue);
    this.form.get('gaiaData.answerStyle').setValue(this.answerStyleValue);
    this.form.get('gaiaData.checkToxic').setValue(false);
    this.form.get('gaiaData.anonymize').setValue(false);
    this.form.get('gaiaData.allowListString').setValue(this.companyName);
  }

  /**
   * Must not be called in edition if third party
   */
  private initIsGaia() {
    this.alcmeonAiService.isGaiaConfigExists().subscribe({
      next: isGaiaExists => {
        this.isGaiaExists = isGaiaExists;
        const newTypeItems = this.typeItems.map(item =>
          item.id === 0 ? { ...item, disabled: !this.isGaiaExists } : item
        );
        this.typeItems = newTypeItems;

        // Init Type
        if (this.isGaiaExists) {
          this.form.get('type').setValue(0);
          this.alcmeonAiService
            .getKnowledgeDocuments()
            .pipe(finalize(() => this.basicService.hideSpinner()))
            .subscribe(docs => {
              this.knowledgeDocs = (docs || []).filter(doc => doc.isActive);
              this.knowledgeDocs.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
              this.knowledgeDocsItems = this.knowledgeDocs.map(doc => ({ id: doc.uuid, label: doc.name }));
            });
        } else {
          this.form.get('type').setValue(1);
          this.basicService.hideSpinner();
        }
      },
      error: () => {
        // if edition, type is already set so don't touch it
        if (this.isCreation) {
          this.form.get('type').setValue(1);
        }
        this.basicService.hideSpinner();
      },
    });
  }

  private updateFooter(aiAgent: AiAgent) {
    this.footerContent = {
      createdBy: this.usersMap.get(aiAgent.createdBy)?.realname,
      createdAt: aiAgent.createdAt,
      modifiedBy: this.usersMap.get(aiAgent.updatedBy)?.realname,
      modifiedAt: aiAgent.updatedAt,
    };
  }

  private enableGaia() {
    this.form.get('gaiaData').enable({ onlySelf: true });

    if (this.form.get('mode').value === AiAgentModeEnum.aissitant) {
      this.form.get('gaiaData.checkToxic').disable({ onlySelf: true });
    }

    if (!this.form.get('gaiaData.anonymize').value) {
      this.form.get('gaiaData.allowListString').disable({ onlySelf: true });
    }

    this.form.get('externalUrl').disable({ onlySelf: true });
    this.form.get('apiKey').disable({ onlySelf: true });
  }

  private enableThirdParty() {
    this.form.get('externalUrl').enable({ onlySelf: true });
    this.form.get('apiKey').enable({ onlySelf: true });

    this.form.get('gaiaData').disable({ onlySelf: true });
  }

  copyPublicKey() {
    this.clipboard.copy(this.form.get('publicKey').value);
    this.basicService.toastSuccess('parameters/alcmeon-ai/edit-agent/public-key-copied');
  }

  goToAiAgents() {
    this.customRouter.go('auth.alcmeon.fuse.parameters.alcmeon-ai.agents');
  }

  save() {
    this.aiAgent = this.form.value;

    if (this.form.get('type').value === 0) {
      if (this.form.get('gaiaData.anonymize').value) {
        const arrayAllowList = this.form.get('gaiaData.allowListString').value.split('\n');
        this.aiAgent.gaiaData.allowList = arrayAllowList.filter(word => word);
      } else {
        this.form.get('gaiaData.allowListString').setValue('');
        this.aiAgent.gaiaData.allowList = [''];
      }

      delete this.aiAgent.gaiaData.allowListString;
    }

    this.basicService.showSpinner();
    if (this.isCreation) {
      this.alcmeonAiService
        .createAiAgent(this.aiAgent)
        .pipe(finalize(() => this.basicService.hideSpinner()))
        .subscribe({
          complete: () => this.basicService.toastSuccess('parameters/alcmeon-ai/edit-agent/create-success'),
          next: () => {
            this.goToAiAgents();
          },
        });
    } else {
      this.alcmeonAiService
        .updateAiAgent(this.id, this.aiAgent)
        .pipe(finalize(() => this.basicService.hideSpinner()))
        .subscribe({
          complete: () => this.basicService.toastSuccess('parameters/alcmeon-ai/edit-agent/edit-success'),
          next: aiAgent => {
            this.updateFooter(aiAgent);
          },
        });
    }

    this.form.markAsPristine();
  }

  ngOnDestroy(): void {
    this.subscriptions?.unsubscribe();
  }
}
